import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { createApiUrl } from '../config/api';

function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    console.log('🔍 ResetPassword - token from URL:', token);
    console.log('🔍 ResetPassword - searchParams:', searchParams.toString());
    
    if (!token) {
      console.warn('❌ No reset token found, redirecting to login');
      toast.error('Invalid or missing reset token. Please request a new password reset.');
      navigate('/login');
    } else {
      console.log('✅ Reset token found:', token);
    }
  }, [token, navigate, searchParams]);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!password || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(createApiUrl('auth/reset-password'), {
        token: token,
        password: password
      });
      
      if (response.data.success) {
        toast.success('Password reset successfully! Please sign in with your new password.');
        navigate('/login');
      } else {
        throw new Error(response.data.error || 'Failed to reset password');
      }
    } catch (err) {
      console.error('Password reset failed:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to reset password';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return null;
  }

  return (
    <div className="min-h-screen bg-dark-100 font-sans antialiased flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-sm">
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 bg-gradient-to-br from-accent-primary to-accent-tertiary rounded-xl flex items-center justify-center shadow-lg">
            <div className="text-white font-bold text-xl tracking-wider">H</div>
          </div>
        </div>
        <h1 className="text-3xl font-light text-dark-800 mb-4 tracking-tight leading-tight text-center">
          Reset Your Password
        </h1>
        <p className="text-base text-dark-600 leading-relaxed font-light text-center max-w-sm mx-auto mb-6">
          Enter your new password below to complete the reset process.
        </p>
      </div>

      <div className="mt-6 sm:mx-auto sm:w-full sm:max-w-sm">
        <div className="bg-dark-200/95 backdrop-blur-xl border border-dark-400/60 rounded-xl shadow-lg overflow-hidden">
          <div className="px-6 py-8">
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-dark-700 mb-3 tracking-tight">
                  New Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full bg-white/80 dark:bg-slate-900/80 border-2 border-slate-200/60 dark:border-slate-700/60 rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-slate-900/20 focus:ring-offset-2 dark:focus:ring-slate-100/20 dark:focus:ring-offset-slate-900 focus:border-slate-900/40 dark:focus:border-slate-100/40 text-sm transition-all duration-200 text-slate-900 dark:text-slate-100 tracking-tight leading-relaxed backdrop-blur-sm placeholder-slate-400 dark:placeholder-slate-500"
                  placeholder="Enter new password"
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-dark-700 mb-3 tracking-tight">
                  Confirm New Password
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="block w-full bg-white/80 dark:bg-slate-900/80 border-2 border-slate-200/60 dark:border-slate-700/60 rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-slate-900/20 focus:ring-offset-2 dark:focus:ring-slate-100/20 dark:focus:ring-offset-slate-900 focus:border-slate-900/40 dark:focus:border-slate-100/40 text-sm transition-all duration-200 text-slate-900 dark:text-slate-100 tracking-tight leading-relaxed backdrop-blur-sm placeholder-slate-400 dark:placeholder-slate-500"
                  placeholder="Confirm new password"
                />
              </div>

              {error && (
                <div className="rounded-xl bg-red-900/20 border border-red-800/60 p-4 backdrop-blur-sm">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      <div className="w-8 h-8 bg-red-900/40 rounded-xl flex items-center justify-center">
                        <svg className="h-4 w-4 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-red-200 leading-relaxed">
                        {error}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full inline-flex justify-center items-center px-8 py-4 bg-accent-primary hover:bg-accent-tertiary text-white text-sm font-medium focus:outline-none focus:ring-2 focus:ring-accent-primary/20 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 rounded-xl shadow-sm tracking-tight leading-relaxed"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Resetting Password...
                  </>
                ) : 'Reset Password'}
              </button>
            </form>
            
            <div className="text-center pt-4">
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 font-medium transition-colors duration-200 tracking-tight"
              >
                Back to Sign In
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;